/**
 * Provedores de bots — dados baseados na Wiki do xat (wiki.xat.com/Bot)
 * e sites oficiais quando a Wiki aponta para eles.
 * Benefícios são únicos por provedor (sem repetir o mesmo texto entre cards).
 */

export type BotProviderStatId = 'availability' | 'trial' | 'multilingual';

export type BotProviderStat = {
  id: BotProviderStatId;
  label: string;
  value: string;
};

export type BotProviderBenefit = {
  id: string;
  title: string;
  description: string;
};

export type BotProviderOwner = {
  /** Regname xat do responsável (Wiki / Contact Person). */
  name: string;
  xatspaceUrl: string;
};

export type BotProviderNavItem = {
  id: string;
  label: string;
  href: string;
  external?: boolean;
};

export type BotProvider = {
  id: string;
  name: string;
  tagline: string;
  /** Logo em /public/bot — null = placeholder. */
  logoUrl: string | null;
  websiteUrl: string;
  websiteLabel: string;
  chatUrl: string | null;
  owner: BotProviderOwner | null;
  stats: BotProviderStat[];
  benefits: BotProviderBenefit[];
  nav: BotProviderNavItem[];
};

export const BOT_PROVIDERS_SECTION = {
  eyebrow: 'Provedores de bots',
  title: 'Encontre o provedor ideal para o seu chat',
  description:
    'Conheça os provedores disponíveis para a comunidade xat e compare recursos, períodos de teste e condições de uso — tudo em um só lugar, para escolher com confiança.',
  disclaimer:
    'Os provedores listados são serviços independentes. O xat e o xat Ajuda não os operam nem endossam oficialmente. Avalie cada serviço antes de usar.',
} as const;

export const BOT_PROVIDERS: BotProvider[] = [
  {
    id: 'arcbots',
    name: 'ARCbots',
    tagline: 'Painel moderno para moderação e engajamento, com trial de 120 horas.',
    logoUrl: '/bot/arcbots.png',
    websiteUrl: 'https://panel.arcbots.com/page/register.html',
    websiteLabel: 'arcbots.com',
    chatUrl: 'https://xat.com/ARCbot',
    owner: {
      name: 'Actavus',
      xatspaceUrl: 'https://xat.me/Actavus',
    },
    stats: [
      { id: 'availability', label: 'Disponibilidade', value: 'Gratuito*' },
      { id: 'trial', label: 'Tempo de teste', value: '120 horas' },
      { id: 'multilingual', label: 'Multilíngue', value: 'Sim' },
    ],
    benefits: [
      {
        id: 'arc-trial',
        title: '120 horas de teste',
        description: 'Cinco dias de trial para configurar moderação e engajamento sem compromisso.',
      },
      {
        id: 'arc-staff',
        title: 'Grátis para staff oficial (com regra)',
        description:
          'Equipe do chat oficial pode obter tempo gratuito se um ARCbot estiver presente e o mod tiver pelo menos um dia de cargo (comando !myaccount).',
      },
      {
        id: 'arc-volunteers',
        title: 'Voluntários e staff ARCbot',
        description: 'Voluntários do xat e a própria equipe ARCbot têm acesso às condições gratuitas do serviço.',
      },
      {
        id: 'arc-ep',
        title: 'Conta ARCbot com EveryPower',
        description: 'Conta ARCbot (1510151) disponibilizada com EveryPower para o serviço.',
      },
    ],
    nav: [
      { id: 'register', label: 'Registro', href: 'https://panel.arcbots.com/page/register.html', external: true },
      { id: 'commands', label: 'Comandos', href: 'https://panel.arcbots.com/info/commands.html', external: true },
      { id: 'support', label: 'Suporte', href: 'https://panel.arcbots.com/panel/ticket.html', external: true },
      { id: 'chat', label: 'Chat', href: 'https://xat.com/ARCbot', external: true },
    ],
  },
  {
    id: 'fexbots',
    name: 'FEXBots',
    tagline: 'Plano básico gratuito, trial de 96 horas e recompensa por sugestões implementadas.',
    logoUrl: '/bot/fexbots.png',
    websiteUrl: 'https://fexbots.com/trial',
    websiteLabel: 'fexbots.com',
    chatUrl: 'https://xat.com/FEXBots',
    owner: {
      name: 'Jadiction',
      xatspaceUrl: 'https://xat.me/Jadiction',
    },
    stats: [
      { id: 'availability', label: 'Disponibilidade', value: 'Gratuito*' },
      { id: 'trial', label: 'Tempo de teste', value: '96 horas' },
      { id: 'multilingual', label: 'Multilíngue', value: 'Sim' },
    ],
    benefits: [
      {
        id: 'fex-basic',
        title: 'Uso básico com recursos limitados',
        description:
          'Há um modo gratuito com funcionalidades reduzidas — compare free vs paid no site do provedor.',
      },
      {
        id: 'fex-promo',
        title: 'Oficiais, tributos e chats promovidos',
        description: 'Chats oficiais, de tributo e promovidos entram nas condições gratuitas do FEXBots.',
      },
      {
        id: 'fex-bugs',
        title: 'Bugs e sugestões implementadas',
        description: 'Quem envia melhorias que são adotadas pode receber acesso gratuito.',
      },
      {
        id: 'fex-ep',
        title: 'Conta FEXBot com EveryPower',
        description: 'Conta FEXBot (23232323) disponibilizada com EveryPower.',
      },
    ],
    nav: [
      { id: 'trial', label: 'Trial / Registro', href: 'https://fexbots.com/trial', external: true },
      { id: 'commands', label: 'Comandos', href: 'https://fexbots.com/commands', external: true },
      { id: 'support', label: 'Suporte', href: 'https://xat.com/FEXBots', external: true },
      { id: 'compare', label: 'Free vs Paid', href: 'https://fexbots.com/tools/freevspaid', external: true },
    ],
  },
  {
    id: 'lp89',
    name: 'LP89',
    tagline: 'Trial sob aprovação, quatro horas de uso contínuo e sem opção de pausa.',
    logoUrl: '/bot/lp89.png',
    websiteUrl: 'https://lp89.it',
    websiteLabel: 'lp89.it',
    chatUrl: 'https://xat.com/LP89',
    owner: {
      name: 'xmaniaco89',
      xatspaceUrl: 'https://xat.me/xmaniaco89',
    },
    stats: [
      { id: 'availability', label: 'Disponibilidade', value: 'Gratuito*' },
      { id: 'trial', label: 'Tempo de teste', value: '48 horas' },
      { id: 'multilingual', label: 'Multilíngue', value: 'Sim' },
    ],
    benefits: [
      {
        id: 'lp-approval',
        title: 'Trial apenas com aprovação',
        description:
          'As 48 horas de teste precisam ser pedidas no painel; o provedor decide se você é elegível.',
      },
      {
        id: 'lp-4h',
        title: 'Quatro horas seguidas de uso completo',
        description:
          'Nas condições gratuitas: quatro horas consecutivas, reinícios ilimitados e uso completo — exceto jogos.',
      },
      {
        id: 'lp-ep-premium',
        title: 'EveryPower disponível',
        description:
          'O bot LP89 (356566558) possui EveryPower disponível para usuários premium ou chats oficiais.',
      },
      {
        id: 'lp-no-pause',
        title: 'Sem pausa do bot',
        description: 'Diferente de outros provedores, não oferece pausa.',
      },
    ],
    nav: [
      { id: 'site', label: 'Site', href: 'https://lp89.it', external: true },
      { id: 'commands', label: 'Comandos', href: 'https://lp89.it/Bot/cmd.php', external: true },
      { id: 'support', label: 'Tickets', href: 'https://lp89.it/Ticket/', external: true },
      { id: 'chat', label: 'Chat', href: 'https://xat.com/LP89', external: true },
    ],
  },
  {
    id: 'oceanbot',
    name: 'OceanBot',
    tagline: 'Vários bots por conta, partilha entre contas e trial de 168 horas.',
    logoUrl: null,
    websiteUrl: 'https://oceanbot.net/panel/user/register',
    websiteLabel: 'Criar conta',
    chatUrl: 'https://xat.com/OceanBot',
    owner: {
      name: 'Paulo',
      xatspaceUrl: 'https://xat.me/Paulo',
    },
    stats: [
      { id: 'availability', label: 'Disponibilidade', value: 'Gratuito*' },
      { id: 'trial', label: 'Tempo de teste', value: '168 horas' },
      { id: 'multilingual', label: 'Multilíngue', value: 'Sim' },
    ],
    benefits: [
      {
        id: 'oc-unlimited',
        title: 'Bots ilimitados por conta',
        description: 'Gerencie quantos bots precisar na mesma conta OceanBot.',
      },
      {
        id: 'oc-share',
        title: 'Partilha com várias contas',
        description: 'O mesmo bot pode ser partilhado entre múltiplas contas xat.',
      },
      {
        id: 'oc-nopowers',
        title: 'Grátis para bots sem powers',
        description: 'Bots sem powers entram nas condições gratuitas, além de oficiais/tributos e voluntários.',
      },
      {
        id: 'oc-trial',
        title: '168 horas de teste',
        description: 'O trial mais longo listado na Wiki entre estes provedores.',
      },
    ],
    nav: [
      { id: 'register', label: 'Registro', href: 'https://oceanbot.net/panel/user/register', external: true },
      { id: 'commands', label: 'Comandos', href: 'https://oceanbot.net/panel/commands/', external: true },
      { id: 'support', label: 'Suporte', href: 'https://oceanbot.net/panel/support/list', external: true },
      { id: 'chat', label: 'Chat', href: 'https://xat.com/OceanBot', external: true },
    ],
  },
  {
    id: 'pythbots',
    name: 'PythBots',
    tagline: 'Setup rápido, trial de 3 dias e API documentada para integrações.',
    logoUrl: '/bot/pythbots.jpeg',
    websiteUrl: 'https://www.pythbots.com/',
    websiteLabel: 'pythbots.com',
    chatUrl: 'https://xat.com/Pythbots',
    owner: {
      name: 'Mathias',
      xatspaceUrl: 'https://xat.me/Mathias',
    },
    stats: [
      { id: 'availability', label: 'Disponibilidade', value: 'Pago + trial' },
      { id: 'trial', label: 'Tempo de teste', value: '72 horas' },
      { id: 'multilingual', label: 'Multilíngue', value: 'Não' },
    ],
    benefits: [
      {
        id: 'pyth-api',
        title: 'API para programadores',
        description: 'Acesso documentado à API do bot para dashboards e integrações — diferencial público do PythBots.',
      },
      {
        id: 'pyth-setup',
        title: 'No ar em poucos minutos',
        description: 'O provedor destaca setup em cerca de dois minutos: criar conta, atribuir o bot e começar.',
      },
      {
        id: 'pyth-trial',
        title: '3 dias de teste',
        description: 'Trial de 72 horas para validar moderação e automação no seu chat real.',
      },
      {
        id: 'pyth-support',
        title: 'Suporte humano no horário do chat',
        description: 'Equipe disponível para ajudar quando o chat está ativo, inclusive em horários da tarde.',
      },
    ],
    nav: [
      { id: 'site', label: 'Site', href: 'https://www.pythbots.com/', external: true },
      { id: 'chat', label: 'Chat', href: 'https://xat.com/Pythbots', external: true },
      { id: 'privacy', label: 'Privacidade', href: 'https://www.pythbots.com/privacy.php', external: true },
    ],
  },
  {
    id: 'evoxbot',
    name: 'EVOXBot',
    tagline: 'Pontos, loja de itens e painel simples — trial gratuito de 48 horas.',
    logoUrl: '/bot/evoxbot.png',
    websiteUrl: 'https://evoxbot.com/',
    websiteLabel: 'evoxbot.com',
    chatUrl: null,
    owner: {
      name: 'BrunoT',
      xatspaceUrl: 'https://xat.me/BrunoT',
    },
    stats: [
      { id: 'availability', label: 'Disponibilidade', value: 'Pago + trial' },
      { id: 'trial', label: 'Tempo de teste', value: '48 horas' },
      { id: 'multilingual', label: 'Multilíngue', value: 'Idioma no painel' },
    ],
    benefits: [
      {
        id: 'evox-points',
        title: 'Ranking de pontos e loja',
        description: 'Sistema de pontuação e troca de itens configurável para o chat.',
      },
      {
        id: 'evox-panel',
        title: 'Painel para nome, status e idioma',
        description: 'Altere informações do bot (nome, status, idioma, notificações) pelo dashboard.',
      },
      {
        id: 'evox-commands',
        title: 'Comandos com alias e cargos',
        description: 'Edite aliases, ative/desative comandos e defina o cargo mínimo para cada um.',
      },
      {
        id: 'evox-pay',
        title: 'Tempo sob demanda com xats ou days',
        description: 'Adicione tempo ao bot transferindo a quantidade de xats ou days que quiser.',
      },
    ],
    nav: [
      { id: 'site', label: 'Site', href: 'https://evoxbot.com/', external: true },
      { id: 'trial', label: 'Trial', href: 'https://evoxbot.com/', external: true },
    ],
  },
];
